import { jsx as _jsx } from "react/jsx-runtime";
import lodashGet from 'lodash/get';
import { useMemo } from 'react';
import { IoColorPaletteOutline } from 'react-icons/io5';
import { DropdownMenu } from 'react-science/ui';
import { useDispatch } from '../../context/DispatchContext';
import ReactTable from '../../elements/ReactTable/ReactTable';
import { useAlert } from '../../elements/popup/Alert';
import { usePanelPreferences } from '../../hooks/usePanelPreferences';
import { DELETE_SPECTRA, ORDER_SPECTRA, RECOLOR_SPECTRA_COLOR, } from '../../reducer/types/Types';
import { copyTextToClipboard } from '../../utility/export';
import ColorIndicator from './base/ColorIndicator';
import ShowHideSpectrumButton from './base/ShowHideSpectrumButton';
import { SpectrumName } from './base/SpectrumName';
function formatValueAsHTML(value) {
    if (value) {
        value = value.replace(/(?<value>\d+)/g, '<sub>$<value></sub>');
    }
    return value;
}
function getActiveSpectraAsObject(activeSpectra) {
    const result = {};
    if (activeSpectra) {
        for (const activeSpectrum of activeSpectra) {
            result[activeSpectrum.id] = true;
        }
    }
    return result;
}
const columnStyle = {
    maxWidth: 0,
    overflow: 'hidden',
};
const options = [
    {
        label: 'Recolor based on distinct value',
        type: 'option',
        icon: _jsx(IoColorPaletteOutline, {}),
    },
];
export function SpectraTable(props) {
    const { data, activeSpectra, onChangeVisibility, onOpenSettingModal, onChangeActiveSpectrum, nucleus, } = props;
    const alert = useAlert();
    const dispatch = useDispatch();
    const spectraPreferences = usePanelPreferences('spectra', nucleus);
    const activeSpectraObj = getActiveSpectraAsObject(activeSpectra);
    const COLUMNS = useMemo(() => ({
        visible: {
            id: 'hide-show-spectrum',
            Header: '',
            style: {
                width: '35px',
                maxWidth: '55px',
            },
            Cell: ({ row }) => {
                return (_jsx(ShowHideSpectrumButton, { data: row.original, onChangeVisibility: onChangeVisibility }));
            },
        },
        name: {
            Header: '',
            style: columnStyle,
            accessor: (row) => row.info.name,
            Cell: ({ row }) => {
                return _jsx(SpectrumName, { data: row.original });
            },
        },
        solvent: {
            Header: '',
            style: columnStyle,
            accessor: (row) => row.info.solvent,
            Cell: ({ row }) => {
                const info = row.original.info;
                return (info?.solvent && (_jsx("div", { 
                    // style={styles.info}
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML: {
                        __html: formatValueAsHTML(info.solvent),
                    } })));
            },
        },
        color: {
            id: 'spectrum-actions',
            style: {
                width: '30px',
                maxWidth: '30px',
            },
            Cell: ({ row }) => {
                return (_jsx(ColorIndicator, { display: row.original.display, dimension: row.original.info.dimension, onClick: (event) => onOpenSettingModal(event, row.original) }));
            },
        },
    }), [onChangeVisibility, onOpenSettingModal]);
    const contextMenu = useMemo(() => [
        {
            label: 'Copy to Clipboard',
            onClick: (spectrumData) => {
                void (async () => {
                    const { data, info } = spectrumData;
                    const success = await copyTextToClipboard(JSON.stringify({ data, info }, (_, value) => ArrayBuffer.isView(value) ? Array.from(value) : value, 2));
                    if (success) {
                        alert.success('Data copied to clipboard');
                    }
                    else {
                        alert.error('Copy to clipboard failed');
                    }
                })();
            },
        },
        {
            label: 'Delete',
            onClick: (spectrumData) => {
                setTimeout(() => {
                    dispatch({ type: DELETE_SPECTRA, id: spectrumData.id });
                }, 0);
            },
        },
    ], [alert, dispatch]);
    function handleActiveRow(row) {
        return activeSpectraObj?.[row?.original.id] || false;
    }
    const tableColumns = useMemo(() => {
        let columns = [];
        let index = 0;
        const visibleColumns = spectraPreferences.columns.filter((col) => col.visible);
        for (const col of visibleColumns) {
            const name = col?.name;
            const path = col?.jpath;
            if (name && COLUMNS[name]) {
                columns.push({
                    ...COLUMNS[name],
                    Header: () => _jsx(ColumnHeader, { label: col.label, col: col }),
                    id: name,
                    style: name === 'name' && visibleColumns.length > 3
                        ? {
                            ...COLUMNS[name].style,
                            width: '50%',
                        }
                        : COLUMNS[name].style,
                });
            }
            else {
                columns.push({
                    Header: () => _jsx(ColumnHeader, { label: col.label, col: col }),
                    accessor: (row) => lodashGet(row, path, ''),
                    id: `${index}`,
                    style: columnStyle,
                });
            }
            index++;
        }
        return columns;
    }, [COLUMNS, spectraPreferences.columns]);
    function handleSortEnd(data) {
        dispatch({
            type: ORDER_SPECTRA,
            payload: {
                data,
            },
        });
    }
    function handleRowStyle(data) {
        return {
            base: activeSpectraObj?.[data?.original.id] ? { opacity: 0.2 } : {},
            activated: { opacity: 1 },
        };
    }
    return (_jsx(ReactTable, { rowStyle: handleRowStyle, activeRow: handleActiveRow, data: data, columns: tableColumns, onClick: (e, data) => onChangeActiveSpectrum(e, data.original), enableVirtualScroll: true, approxItemHeight: 26, context: contextMenu, onSortEnd: handleSortEnd, style: { 'table td': { paddingTop: 0, paddingBottom: 0 } } }));
}
const ColumnHeader = ({ label, col, }) => {
    const dispatch = useDispatch();
    function selectHandler() {
        if (col?.jpath) {
            dispatch({ type: RECOLOR_SPECTRA_COLOR, payload: { jpath: col?.jpath } });
        }
    }
    return (_jsx(DropdownMenu, { trigger: "contextMenu", options: options, onSelect: selectHandler, children: _jsx("div", { style: {
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
            }, children: label }) }));
};
